import {
  createSlice,
  createSelector,
  createAction,
  PayloadAction,
} from '@reduxjs/toolkit';

import {
  MetaDataType,
  SortableFields,
  SortOrder,
  UUID,
} from 'src/types/utility';
import { RootState } from 'src/redux/store';
import { MODAL_STATUS } from 'src/components/Modal/constants';
import { ListUserType, User, UserGroups } from 'src/types/users';
import { FetchUsersResponse, FetchUserTypesResponse } from 'src/services/types';
import { DATA_STATE_KEY, DATA_FETCHING_STATUS } from '../../constants';
import { mergeArraysWithUniqueIds } from '../../dataUtils';
import { ActiveUserWithStatus } from './types';
import { parseUserTypesResponse, parseAllUsersResponse } from './parser';
import { TableParamsType } from 'src/types/table';

export const STATE_KEY = 'user';

export const INITIAL_STATE: {
  userTypes: {
    data: ListUserType[];
    status: keyof typeof DATA_FETCHING_STATUS | null;
  };
  userList: {
    data: ActiveUserWithStatus[];
    metadata: MetaDataType;
  };
  mtmUsersList: {
    data: ActiveUserWithStatus[];
    status: keyof typeof DATA_FETCHING_STATUS | null;
  };
  userGroups: {
    data: UserGroups[];
    status: keyof typeof DATA_FETCHING_STATUS | null;
  };
  status: keyof typeof DATA_FETCHING_STATUS | null;
  modalStatus: MODAL_STATUS;
} = {
  userTypes: { data: [], status: null },
  userList: {
    data: [],
    metadata: {
      page: {
        totalResults: 0,
        page: 0,
        limit: 0,
      },
      sort: [
        {
          order: SortOrder.asc,
          prop: SortableFields.status,
        },
      ],
    },
  },
  mtmUsersList: { data: [], status: null },
  userGroups: { data: [], status: null },
  status: null,
  modalStatus: MODAL_STATUS.INITIAL,
};

const slice = createSlice({
  name: STATE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    fetchExistentUsersSuccess(
      state,
      action: PayloadAction<{
        users: FetchUsersResponse;
      }>,
    ) {
      const { users } = action.payload;

      state.userList.data = mergeArraysWithUniqueIds(
        state.userList.data,
        parseAllUsersResponse(users.data),
      );
      state.status = DATA_FETCHING_STATUS.SUCCESS;
    },
    fetchAllUsersSuccess(
      state,
      action: PayloadAction<{
        data: User[];
        metadata: MetaDataType;
      }>,
    ) {
      const { data, metadata } = action.payload;

      state.userList.data = parseAllUsersResponse(data);
      state.userList.metadata = metadata;
      state.status = DATA_FETCHING_STATUS.SUCCESS;
    },
    fetchExistentUsersFail(state) {
      state.status = DATA_FETCHING_STATUS.ERROR;
    },
    fetchUserTypes(state) {
      state.userTypes.status = DATA_FETCHING_STATUS.LOADING;
      state.userTypes = INITIAL_STATE.userTypes;
    },
    fetchUserTypesSuccess(
      state,
      action: PayloadAction<FetchUserTypesResponse>,
    ) {
      state.userTypes.data = parseUserTypesResponse(action.payload.data);
      state.userTypes.status = DATA_FETCHING_STATUS.SUCCESS;
    },
    fetchUserGroupsSuccess(state, action: PayloadAction<UserGroups[]>) {
      state.userGroups.data = action.payload;
    },
    setUserGroupStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.userGroups.status = action.payload;
    },
    fetchUserTypesFail(state) {
      state.userTypes.status = DATA_FETCHING_STATUS.ERROR;
    },
    setResetUsersState(state) {
      state.userList.data = [];
      state.userList.metadata = {
        page: {
          totalResults: 0,
          page: 0,
          limit: 0,
        },
        sort: [
          {
            order: SortOrder.asc,
            prop: SortableFields.status,
          },
        ],
      };
    },
    setStatus(state, action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>) {
      state.status = action.payload;
    },
    setModalStatus(state, action: PayloadAction<MODAL_STATUS>) {
      state.modalStatus = action.payload;
    },
    deleteUserSuccess(state, action: PayloadAction<UUID>) {
      const deletedId = action.payload;
      state.userList.data = state.userList.data.filter(
        user => user.id !== deletedId,
      );
    },
  },
  extraReducers: {},
});

export const getState = (state: RootState) =>
  state[DATA_STATE_KEY][STATE_KEY] || INITIAL_STATE;

const getUserId = (_state: RootState, userId: UUID) => userId;

export const selectors = {
  getUsersMetadata: createSelector(getState, state => state.userList.metadata),
  getUsersList: createSelector(getState, state => state.userList.data),
  getMtmUsers: createSelector(getState, state => state.mtmUsersList),
  selectUser: createSelector(getState, getUserId, (state, userId) =>
    state.userList.data.find(user => user.id === userId),
  ),
  getStatus: createSelector(getState, state => state.status),
  getUserTypes: createSelector(getState, state => state.userTypes.data),
  getModalStatus: createSelector(getState, state => state.modalStatus),
  getUserMainGroup: createSelector(getState, state =>
    state.userGroups.data.filter(group => !!group.isMainGroup),
  ),
};

export type fetchAllUsersProps = {
  page?: number;
  limit?: number;
  customerID?: UUID;
  searchKeyword?: string;
  cache?: string;
  sortBy?: TableParamsType['sortBy'][];
};

const extraActions = {
  fetchUsers: createAction(`${STATE_KEY}/fetchUsers`),
  fetchExistentUsers: createAction(`${STATE_KEY}/fetchExistentUsers`),
  fetchAllUsers: createAction<fetchAllUsersProps | null | undefined>(
    `${STATE_KEY}/fetchAllUsers`,
  ),
  inviteUser: createAction<{
    formData: Record<string, unknown>;
    customerId?: UUID;
  }>(`${STATE_KEY}/inviteUser`),
  toggleUserActivation: createAction<{
    userId: UUID;
    locked: boolean;
    customerId?: UUID;
  }>(`${STATE_KEY}/toggleUserActivation`),
  deleteUser: createAction<UUID>(`${STATE_KEY}/deleteUser`),
  updateUser: createAction<{
    userId: UUID;
    customerId?: UUID;
    data: Record<string, unknown>;
    userStatus?: string;
  }>(`${STATE_KEY}/updateUser`),
  updateMfaUserInfo: createAction<{
    userId: UUID;
    data: Record<string, unknown>;
  }>(`${STATE_KEY}/updateMfaUserInfo`),
  updateUserPhone: createAction<{ userId: UUID; data: { phone: string } }>(
    `${STATE_KEY}/updatePhone`,
  ),
  updateCurrentUserLocales: createAction<{
    locale: string;
  }>(`${STATE_KEY}/updateUserLocales`),
  sendMessageForResetPassword: createAction<{ email: string }>(
    `${STATE_KEY}/sendMessageForResetPassword`,
  ),
  resendInvitation: createAction<{ id: string }>(
    `${STATE_KEY}/resendInvitation`,
  ),
  getUserGroups: createAction(`${STATE_KEY}/getUserGroups`),
  deleteInvitation: createAction<UUID>(`${STATE_KEY}/deleteInvitation`),
};

export const actions = { ...slice.actions, ...extraActions };

const { reducer } = slice;
export default reducer;
